import { Matrix3D, Matrix, Point, Vector3D, Rectangle, ColorUtils, CoordinateSystem } from '@awayjs/core';
import { BitmapImage2D } from '../image/BitmapImage2D';
import { ContextGLBlendFactor } from '../base/ContextGLBlendFactor';
import { ContextGLClearMask } from '../base/ContextGLClearMask';
import { ContextGLCompareMode } from '../base/ContextGLCompareMode';
import { ContextGLProgramType } from '../base/ContextGLProgramType';
import { ContextGLTriangleFace } from '../base/ContextGLTriangleFace';
import { ContextGLStencilAction } from '../base/ContextGLStencilAction';
import { IndexBufferSoftware } from './IndexBufferSoftware';
import { VertexBufferSoftware } from './VertexBufferSoftware';
import { TextureSoftware } from './TextureSoftware';
import { CubeTextureSoftware } from './CubeTextureSoftware';
import { ProgramSoftware } from './ProgramSoftware';
import { SoftwareSamplerState } from './SoftwareSamplerState';
import { BlendModeSoftware } from './BlendModeSoftware';
import { DepthCompareModeSoftware } from './DepthCompareModeSoftware';
var ContextSoftware = /** @class */ (function () {
    function ContextSoftware(canvas) {
        this._backBufferRect = new Rectangle();
        this._cullingMode = ContextGLTriangleFace.BACK;
        this._blendSource = ContextGLBlendFactor.ONE;
        this._blendDestination = ContextGLBlendFactor.ZERO;
        this._colorMaskR = true;
        this._colorMaskG = true;
        this._colorMaskB = true;
        this._colorMaskA = true;
        this._writeDepth = true;
        this._depthCompareMode = ContextGLCompareMode.LESS;
        this._depthCompareModeSoftware = function (fragDepth, currentDepth) { return void []; };
        this._blendModeSoftware = function (result, dest, source) { return void []; };
        this._screenMatrix = new Matrix3D();
        this._frontBufferMatrix = new Matrix();
        this._bboxMin = new Point();
        this._bboxMax = new Point();
        this._clamp = new Point();
        this._samplerStates = [];
        this._textures = [];
        this.textureBuffersColor = [];
        this.textureBuffersZ = [];
        this.textureBuffersZClear = [];
        this._vertexBuffers = [];
        this._vertexBufferOffsets = [];
        this._vertexBufferFormats = [];
        this._rgba = new Uint8ClampedArray(4);
        this._source = new Uint8ClampedArray(4);
        this._dest = new Uint8ClampedArray(4);
        this._destComp = new Uint8ClampedArray(4);
        this._sourceComp = new Uint8ClampedArray(4);
        //public static _drawCallback:Function = null;
        this._antialias = 0;
        this.hasVao = false;
        this._p0 = new Vector3D();
        this._p1 = new Vector3D();
        this._p2 = new Vector3D();
        this._project = new Vector3D();
        this._barycentric = new Vector3D();
        this._barycentricRight = new Vector3D();
        this._barycentricBottom = new Vector3D();
        this._canvas = canvas;
        this._backBufferColor = new BitmapImage2D(100, 100, false, 0, false);
        this._frontBuffer = new BitmapImage2D(100, 100, false, 0, false);
        this._activeBufferColor = this._backBufferColor;
        var len = 100 * 100;
        var zbufferBytes = new ArrayBuffer(len * 8);
        this._backBufferZ = new Float32Array(zbufferBytes, 0, len);
        this._backBufferZClear = new Float32Array(zbufferBytes, len * 4, len);
        for (var i = 0; i < len; i++)
            this._backBufferZClear[i] = 10000000;
        this._activeBufferZ = this._backBufferZ;
        if (document && document.body) {
            this._frontCanvas = document.createElement('canvas');
            this._frontBufferContext = this._frontCanvas.getContext('2d');
            this._frontBufferImageData = this._frontBufferContext.getImageData(0, 0, 100, 100);
            // TODO: remove software renderToTexture
            this._frontCanvas.style.position = 'absolute';
            document.body.appendChild(this._frontCanvas);
        }
        this._depthCompareModeSoftware[ContextGLCompareMode.ALWAYS] = DepthCompareModeSoftware.always;
        this._depthCompareModeSoftware[ContextGLCompareMode.EQUAL] = DepthCompareModeSoftware.equal;
        this._depthCompareModeSoftware[ContextGLCompareMode.GREATER] = DepthCompareModeSoftware.greaterEqual;
        this._depthCompareModeSoftware[ContextGLCompareMode.GREATER_EQUAL] = DepthCompareModeSoftware.greater;
        this._depthCompareModeSoftware[ContextGLCompareMode.LESS] = DepthCompareModeSoftware.less;
        this._depthCompareModeSoftware[ContextGLCompareMode.LESS_EQUAL] = DepthCompareModeSoftware.lessEqual;
        this._depthCompareModeSoftware[ContextGLCompareMode.NEVER] = DepthCompareModeSoftware.never;
        this._depthCompareModeSoftware[ContextGLCompareMode.NOT_EQUAL] = DepthCompareModeSoftware.notEqual;
        this._blendModeSoftware[ContextGLBlendFactor.DESTINATION_ALPHA] = BlendModeSoftware.destinationAlpha;
        this._blendModeSoftware[ContextGLBlendFactor.DESTINATION_COLOR] = BlendModeSoftware.destinationColor;
        this._blendModeSoftware[ContextGLBlendFactor.ONE] = BlendModeSoftware.one;
        this._blendModeSoftware[ContextGLBlendFactor.ONE_MINUS_DESTINATION_ALPHA] = BlendModeSoftware.oneMinusDestinationAlpha;
        this._blendModeSoftware[ContextGLBlendFactor.ONE_MINUS_DESTINATION_COLOR] = BlendModeSoftware.oneMinusDestinationColor;
        this._blendModeSoftware[ContextGLBlendFactor.ONE_MINUS_SOURCE_ALPHA] = BlendModeSoftware.oneMinusSourceAlpha;
        this._blendModeSoftware[ContextGLBlendFactor.ONE_MINUS_SOURCE_COLOR] = BlendModeSoftware.oneMinusSourceColor;
        this._blendModeSoftware[ContextGLBlendFactor.SOURCE_ALPHA] = BlendModeSoftware.sourceAlpha;
        this._blendModeSoftware[ContextGLBlendFactor.SOURCE_COLOR] = BlendModeSoftware.sourceColor;
        this._blendModeSoftware[ContextGLBlendFactor.ZERO] = BlendModeSoftware.zero;
    }
    ContextSoftware.prototype.createVao = function () {
        return null;
    };
    Object.defineProperty(ContextSoftware.prototype, "glVersion", {
        get: function () {
            return 1;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ContextSoftware.prototype, "pixelRatio", {
        get: function () {
            return 1;
        },
        enumerable: true,
        configurable: true
    });
    ContextSoftware.prototype.configureBackBuffer = function (width, height, antiAlias, enableDepthAndStencil) {
        this._antialias = antiAlias;
        if (this._antialias % 2 != 0)
            this._antialias = Math.floor(this._antialias - 0.5);
        if (this._antialias == 0)
            this._antialias = 1;
        this._frontBuffer._setSize(width, height);
        this._frontCanvas.width = width;
        this._frontCanvas.height = height;
        this._frontBufferImageData = this._frontBufferContext.getImageData(0, 0, width, height);
        var backBufferWidth = width * this._antialias;
        var backBufferHeight = height * this._antialias;
        //double buffer for fast clearing
        var len = backBufferWidth * backBufferHeight;
        var zbufferBytes = new ArrayBuffer(len * 8);
        this._backBufferZ = new Float32Array(zbufferBytes, 0, len);
        this._backBufferZClear = new Float32Array(zbufferBytes, len * 4, len);
        for (var i = 0; i < len; i++)
            this._backBufferZClear[i] = 10000000;
        if (this._activeBufferColor == this._backBufferColor)
            this._activeBufferZ = this._backBufferZ;
        this._backBufferRect.width = backBufferWidth;
        this._backBufferRect.height = backBufferHeight;
        this._backBufferColor._setSize(backBufferWidth, backBufferHeight);
        this.activateScreenMatrix(this._backBufferRect.width, this._backBufferRect.height, true);
        this._frontBufferMatrix = new Matrix();
        this._frontBufferMatrix.scale(1 / this._antialias, 1 / this._antialias);
    };
    ContextSoftware.prototype.activateScreenMatrix = function (width, height, yflip) {
        var raw = this._screenMatrix._rawData;
        raw[0] = width / 2;
        raw[1] = 0;
        raw[2] = 0;
        raw[3] = 0;
        raw[4] = 0;
        raw[5] = yflip ? -height / 2 : height / 2;
        raw[6] = 0;
        raw[7] = 0;
        raw[8] = 0;
        raw[9] = 0;
        raw[10] = 1;
        raw[11] = 0;
        raw[12] = width / 2;
        raw[13] = height / 2;
        raw[14] = 0;
        raw[15] = 0;
        this._yflip = yflip;
    };
    ContextSoftware.prototype.setRenderToTexture = function (target, enableDepthAndStencil, antiAlias, surfaceSelector, mipmapSelector) {
        // Create texture buffer and screen matrix if needed.
        var textureBufferColor = this.textureBuffersColor[surfaceSelector];
        if (textureBufferColor == null) {
            // TODO: consider transparency prop
            // TODO: consider fill color prop
            // TODO: consider powerOfTwo prop
            textureBufferColor = this.textureBuffersColor[surfaceSelector] = new BitmapImage2D(target.width, target.height, false, 0xFFFFFF, true);
            // TODO: transfer the initial image2D data from the texture to the BitmapImage2D object.
            target.uploadFromArray(new Uint8Array(textureBufferColor.data.buffer));
        }
        else {
            textureBufferColor.fillRect(textureBufferColor.rect, 0xFFFFFF);
        }
        this._activeBufferColor = textureBufferColor;
        this._activeBufferColor.lock();
        var textureBufferZ = this.textureBuffersZ[surfaceSelector];
        var textureBufferZClear = this.textureBuffersZClear[surfaceSelector];
        if (textureBufferZ == null) {
            //double buffer for fast clearing
            var len = target.width * target.height;
            var zbufferBytes = new ArrayBuffer(len * 8);
            textureBufferZ = this.textureBuffersZ[surfaceSelector] = new Float32Array(zbufferBytes, 0, len);
            textureBufferZClear = this.textureBuffersZClear[surfaceSelector] = new Float32Array(zbufferBytes, len * 4, len);
            for (var i = 0; i < len; i++)
                textureBufferZClear[i] = 10000000;
        }
        textureBufferZ.set(textureBufferZClear); //fast memcpy
        this._activeBufferZ = textureBufferZ;
        this.activateScreenMatrix(target.width, target.height, false);
        this._activeTexture = target;
    };
    ContextSoftware.prototype.setRenderToBackBuffer = function () {
        this._activeBufferColor = this._backBufferColor;
        this._activeBufferZ = this._backBufferZ;
        this.activateScreenMatrix(this._backBufferColor.width, this._backBufferColor.height, true);
    };
    ContextSoftware.prototype.copyToTexture = function (target, rect, destPoint) {
    };
    ContextSoftware.prototype.drawIndices = function (mode, indexBuffer, firstIndex, numIndices) {
        if (!this._program)
            return;
        // These are place holders for vertex transformation operations.
        // Additional placeholders are for potentially interpolated values generated by near clipping
        // used to avoid trying to draw behind the camera
        var position0 = new Float32Array(4);
        var position1 = new Float32Array(4);
        var position2 = new Float32Array(4);
        var position3 = new Float32Array(4); // potentially interpolated
        var position4 = new Float32Array(4); // potentially interpolated
        var varying0 = new Float32Array(this._program.numVarying * 4);
        var varying1 = new Float32Array(this._program.numVarying * 4);
        var varying2 = new Float32Array(this._program.numVarying * 4);
        var varying3 = new Float32Array(this._program.numVarying * 4); // potentially interpolated
        var varying4 = new Float32Array(this._program.numVarying * 4); // potentially interpolated
        var incomingVertices = new Array(5);
        var outgoingVertices = new Array(4);
        incomingVertices[0] = position1;
        incomingVertices[1] = position2;
        incomingVertices[2] = position0; // notice position0 in index 2
        incomingVertices[3] = position3;
        incomingVertices[4] = position4;
        var incomingVaryings = new Array(5);
        var outgoingVaryings = new Array(4);
        incomingVaryings[0] = varying1;
        incomingVaryings[1] = varying2;
        incomingVaryings[2] = varying0;
        incomingVaryings[3] = varying3;
        incomingVaryings[4] = varying4;
        // Sweep triangles according to culling mode and process thru vertex shader.
        if (this._yflip && this._cullingMode == ContextGLTriangleFace.BACK || !this._yflip && this._cullingMode == ContextGLTriangleFace.FRONT) {
            for (var i = firstIndex; i < numIndices; i += 3) {
                this._program.vertex(this, indexBuffer.data[indexBuffer.startOffset + i], position0, varying0);
                this._program.vertex(this, indexBuffer.data[indexBuffer.startOffset + i + 1], position1, varying1);
                this._program.vertex(this, indexBuffer.data[indexBuffer.startOffset + i + 2], position2, varying2);
                this._triangle(incomingVertices, outgoingVertices, incomingVaryings, outgoingVaryings);
            }
        }
        else if (this._yflip && this._cullingMode == ContextGLTriangleFace.FRONT || !this._yflip && this._cullingMode == ContextGLTriangleFace.BACK) {
            for (var i = firstIndex; i < numIndices; i += 3) {
                this._program.vertex(this, indexBuffer.data[indexBuffer.startOffset + i + 2], position0, varying0);
                this._program.vertex(this, indexBuffer.data[indexBuffer.startOffset + i + 1], position1, varying1);
                this._program.vertex(this, indexBuffer.data[indexBuffer.startOffset + i], position2, varying2);
                this._triangle(incomingVertices, outgoingVertices, incomingVaryings, outgoingVaryings);
            }
        }
        else if (this._cullingMode == ContextGLTriangleFace.FRONT_AND_BACK || this._cullingMode == ContextGLTriangleFace.NONE) {
            for (var i = firstIndex; i < numIndices; i += 3) {
                this._program.vertex(this, indexBuffer.data[indexBuffer.startOffset + i + 2], position0, varying0);
                this._program.vertex(this, indexBuffer.data[indexBuffer.startOffset + i + 1], position1, varying1);
                this._program.vertex(this, indexBuffer.data[indexBuffer.startOffset + i], position2, varying2);
                this._triangle(incomingVertices, outgoingVertices, incomingVaryings, outgoingVaryings);
                this._program.vertex(this, indexBuffer.data[indexBuffer.startOffset + i], position0, varying0);
                this._program.vertex(this, indexBuffer.data[indexBuffer.startOffset + i + 1], position1, varying1);
                this._program.vertex(this, indexBuffer.data[indexBuffer.startOffset + i + 2], position2, varying2);
                this._triangle(incomingVertices, outgoingVertices, incomingVaryings, outgoingVaryings);
            }
        }
        // Transfer buffer data to texture.
        if (this._activeBufferColor != this._backBufferColor) {
            this._activeBufferColor.unlock();
            this._activeTexture.uploadFromArray(new Uint8Array(this._activeBufferColor.data.buffer));
        }
    };
    ContextSoftware.prototype._triangle = function (incomingVertices, outgoingVertices, incomingVaryings, outgoingVaryings) {
        var numOriginalVerticesInside = 0;
        var numInterpolations = 0;
        var numOutgoingVertices = 0;
        // Check if the vertices are behind the near plane and interpolate
        // new vertices whenever one vertex in a side is in front of the camera AND the other is behind it.
        // Start by considering p0 as the previous vertex.
        var previousVertex = incomingVertices[2]; // p0
        var previousVarying = incomingVaryings[2]; // v0
        var previousVertexIsInside = previousVertex[2] > 0; // z > 0
        if (previousVertexIsInside) { // add the original vertex if its inside
            outgoingVertices[0] = previousVertex;
            outgoingVaryings[0] = previousVarying;
            numOutgoingVertices++;
            numOriginalVerticesInside++;
        }
        for (var i = 0; i < 3; i++) {
            var currentVertex = incomingVertices[i];
            var currentVarying = incomingVaryings[i];
            // Need interpolation?
            var currentVertexIsInside = currentVertex[2] > 0; // z > 0
            if (currentVertexIsInside != previousVertexIsInside) { // if one and only one is true
                // Interpolate vertex pair and create a new vertex.
                // z factor
                var dz_current = Math.abs(currentVertex[2]);
                var dz_previous = Math.abs(previousVertex[2]);
                var factorZ = dz_previous / (dz_previous + dz_current);
                this._interpolateVertexPair(factorZ, currentVertex, previousVertex, incomingVertices[3 + numInterpolations]);
                outgoingVertices[numOutgoingVertices] = incomingVertices[3 + numInterpolations];
                this._interpolateVertexPair(factorZ, currentVarying, previousVarying, incomingVaryings[3 + numInterpolations]);
                outgoingVaryings[numOutgoingVertices] = incomingVaryings[3 + numInterpolations];
                numOutgoingVertices++;
                numInterpolations++;
            }
            if (currentVertexIsInside && i != 2) { // add the original vertex if its inside
                outgoingVertices[numOutgoingVertices] = currentVertex;
                outgoingVaryings[numOutgoingVertices] = currentVarying;
                numOutgoingVertices++;
                numOriginalVerticesInside++;
            }
            previousVertex = currentVertex;
            previousVarying = currentVarying;
            previousVertexIsInside = currentVertexIsInside;
        }
        // Project the resulting vertices as triangles.
        if (numOriginalVerticesInside == 3) { // no clipping
            this._projectTriangle(incomingVertices[0], incomingVertices[1], incomingVertices[2], incomingVaryings[0], incomingVaryings[1], incomingVaryings[2]);
        }
        else if (numOriginalVerticesInside == 2) { // quad clip (triangle is converted into a quad)
            this._projectTriangle(outgoingVertices[0], outgoingVertices[1], outgoingVertices[2], outgoingVaryings[0], outgoingVaryings[1], outgoingVaryings[2]);
            this._projectTriangle(outgoingVertices[0], outgoingVertices[2], outgoingVertices[3], outgoingVaryings[0], outgoingVaryings[2], outgoingVaryings[3]);
        }
        else if (numOriginalVerticesInside == 1) { // simple clip (triangle is reduced in size)
            this._projectTriangle(outgoingVertices[0], outgoingVertices[1], outgoingVertices[2], outgoingVaryings[0], outgoingVaryings[1], outgoingVaryings[2]);
        }
        // else if (numOriginalVerticesInside == 0) { // full clipping (do nothing)
        // }
    };
    ContextSoftware.prototype._projectTriangle = function (position0, position1, position2, varying0, varying1, varying2) {
        // Wrap the vertex transformed positions in Vector3D objects.
        this._p0.x = position0[0];
        this._p0.y = position0[1];
        this._p0.z = position0[2];
        this._p0.w = position0[3];
        this._p1.x = position1[0];
        this._p1.y = position1[1];
        this._p1.z = position1[2];
        this._p1.w = position1[3];
        this._p2.x = position2[0];
        this._p2.y = position2[1];
        this._p2.z = position2[2];
        this._p2.w = position2[3];
        // Reject any invalid vertices.
        if (this._p0.w == 0 || this._p1.w == 0 || this._p2.w == 0) {
            return;
        } // w = 0 represent direction vectors and we want positions
        if (isNaN(this._p0.x) || isNaN(this._p0.y) || isNaN(this._p0.z) || isNaN(this._p0.w)) {
            return;
        }
        if (isNaN(this._p1.x) || isNaN(this._p1.y) || isNaN(this._p1.z) || isNaN(this._p1.w)) {
            return;
        }
        if (isNaN(this._p2.x) || isNaN(this._p2.y) || isNaN(this._p2.z) || isNaN(this._p2.w)) {
            return;
        }
        // Clip space.
        this._p0.z = this._p0.z * 2 - this._p0.w;
        this._p1.z = this._p1.z * 2 - this._p1.w;
        this._p2.z = this._p2.z * 2 - this._p2.w;
        // Perspective divide.
        this._p0.project();
        this._p1.project();
        this._p2.project();
        // Transform into screen space.
        this._project.x = this._p0.w;
        this._project.y = this._p1.w;
        this._project.z = this._p2.w;
        this._p0.w = 1;
        this._p1.w = 1;
        this._p2.w = 1;
        this._p0 = this._screenMatrix.transformVector(this._p0, this._p0);
        this._p1 = this._screenMatrix.transformVector(this._p1, this._p1);
        this._p2 = this._screenMatrix.transformVector(this._p2, this._p2);
        // Prepare rasterization bounds.
        this._bboxMin.x = 1000000;
        this._bboxMin.y = 1000000;
        this._bboxMax.x = -1000000;
        this._bboxMax.y = -1000000;
        this._clamp.x = this._activeBufferColor.width - 1;
        this._clamp.y = this._activeBufferColor.height - 1;
        this._bboxMin.x = Math.max(0, Math.min(this._bboxMin.x, this._p0.x));
        this._bboxMin.y = Math.max(0, Math.min(this._bboxMin.y, this._p0.y));
        this._bboxMin.x = Math.max(0, Math.min(this._bboxMin.x, this._p1.x));
        this._bboxMin.y = Math.max(0, Math.min(this._bboxMin.y, this._p1.y));
        this._bboxMin.x = Math.max(0, Math.min(this._bboxMin.x, this._p2.x));
        this._bboxMin.y = Math.max(0, Math.min(this._bboxMin.y, this._p2.y));
        this._bboxMax.x = Math.min(this._clamp.x, Math.max(this._bboxMax.x, this._p0.x));
        this._bboxMax.y = Math.min(this._clamp.y, Math.max(this._bboxMax.y, this._p0.y));
        this._bboxMax.x = Math.min(this._clamp.x, Math.max(this._bboxMax.x, this._p1.x));
        this._bboxMax.y = Math.min(this._clamp.y, Math.max(this._bboxMax.y, this._p1.y));
        this._bboxMax.x = Math.min(this._clamp.x, Math.max(this._bboxMax.x, this._p2.x));
        this._bboxMax.y = Math.min(this._clamp.y, Math.max(this._bboxMax.y, this._p2.y));
        this._bboxMin.x = Math.floor(this._bboxMin.x);
        this._bboxMin.y = Math.floor(this._bboxMin.y);
        this._bboxMax.x = Math.floor(this._bboxMax.x);
        this._bboxMax.y = Math.floor(this._bboxMax.y);
        /*
            NOTE:
            'simplified cross' product calculations below are inlined cross product calculations that ignore the z value and just use the output z value.
         */
        // Skip invalid bounds.
        var w = this._bboxMax.x - this._bboxMin.x;
        var h = this._bboxMax.y - this._bboxMin.y;
        if (w <= 0 || h <= 0) {
            return;
        }
        // Calculate the area division for all barycentric coordinates.
        var area = (this._p1.x - this._p0.x) * (this._p2.y - this._p0.y) - (this._p1.y - this._p0.y) * (this._p2.x - this._p0.x); // simplified cross
        if (area < 0) {
            return;
        } // skip null areas.
        // Pre-calculate barycentric x and y derivatives (steps).
        var w0_dx = -(this._p2.y - this._p1.y) / area;
        var w1_dx = -(this._p0.y - this._p2.y) / area;
        var w2_dx = -(this._p1.y - this._p0.y) / area;
        var w0_dy = (this._p2.x - this._p1.x) / area;
        var w1_dy = (this._p0.x - this._p2.x) / area;
        var w2_dy = (this._p1.x - this._p0.x) / area;
        // Calculate top left barycentric coordinate.
        var w0 = ((this._p2.x - this._p1.x) * (this._bboxMin.y - this._p1.y) - (this._p2.y - this._p1.y) * (this._bboxMin.x - this._p1.x)) / area; // simplified cross
        var w1 = ((this._p0.x - this._p2.x) * (this._bboxMin.y - this._p2.y) - (this._p0.y - this._p2.y) * (this._bboxMin.x - this._p2.x)) / area; // simplified cross
        var w2 = ((this._p1.x - this._p0.x) * (this._bboxMin.y - this._p0.y) - (this._p1.y - this._p0.y) * (this._bboxMin.x - this._p0.x)) / area; // simplified cross
        // Rasterize.
        var cx = 0;
        var cy = 0;
        for (var x = this._bboxMin.x; x <= this._bboxMax.x; x++) {
            for (var y = this._bboxMin.y; y <= this._bboxMax.y; y++) {
                // Calculate the barycentric weights of the pixel.
                this._barycentric.x = w0 + w0_dx * cx + w0_dy * cy;
                this._barycentric.y = w1 + w1_dx * cx + w1_dy * cy;
                this._barycentric.z = w2 + w2_dx * cx + w2_dy * cy;
                // Step y.
                cy += 1;
                // Skip pixel if its not inside the triangle.
                if (this._barycentric.x < 0 || this._barycentric.y < 0 || this._barycentric.z < 0)
                    continue;
                // Calculate derivative (neighbor) weights.
                if (x != this._bboxMax.x) {
                    this._barycentricRight.x = this._barycentric.x + w0_dx;
                    this._barycentricRight.y = this._barycentric.y + w1_dx;
                    this._barycentricRight.z = this._barycentric.z + w2_dx;
                    this._barycentricRight.x /= this._project.x;
                    this._barycentricRight.y /= this._project.y;
                    this._barycentricRight.z /= this._project.z;
                    this._barycentricRight.scaleBy(1 / (this._barycentricRight.x + this._barycentricRight.y + this._barycentricRight.z));
                }
                if (y != this._bboxMax.y) {
                    this._barycentricBottom.x = this._barycentric.x + w0_dy;
                    this._barycentricBottom.y = this._barycentric.y + w1_dy;
                    this._barycentricBottom.z = this._barycentric.z + w2_dy;
                    this._barycentricBottom.x /= this._project.x;
                    this._barycentricBottom.y /= this._project.y;
                    this._barycentricBottom.z /= this._project.z;
                    this._barycentricBottom.scaleBy(1 / (this._barycentricBottom.x + this._barycentricBottom.y + this._barycentricBottom.z));
                }
                // Interpolate frag depth.
                var index = (x % this._activeBufferColor.width) + y * this._activeBufferColor.width;
                var fragDepth = (this._barycentric.x * this._p0.z + this._barycentric.y * this._p1.z + this._barycentric.z * this._p2.z) / (this._barycentric.x + this._barycentric.y + this._barycentric.z);
                this._barycentric.x /= this._project.x;
                this._barycentric.y /= this._project.y;
                this._barycentric.z /= this._project.z;
                this._barycentric.scaleBy(1 / (this._barycentric.x + this._barycentric.y + this._barycentric.z));
                // Depth test.
                if (!this._depthCompareModeSoftware[this._depthCompareMode](fragDepth, this._activeBufferZ[index]))
                    continue;
                // Process fragment shader.
                var fragmentVO = this._program.fragment(this, this._barycentric, this._barycentricRight, this._barycentricBottom, varying0, varying1, varying2, fragDepth);
                if (fragmentVO.discard)
                    continue;
                // Write z buffer.
                if (this._writeDepth)
                    this._activeBufferZ[index] = fragDepth; // TODO: fragmentVO.outputDepth?
                // Write to source and transform color space.
                this._source[0] = fragmentVO.outputColor[0] * 0xFF;
                this._source[1] = fragmentVO.outputColor[1] * 0xFF;
                this._source[2] = fragmentVO.outputColor[2] * 0xFF;
                this._source[3] = fragmentVO.outputColor[3] * 0xFF;
                // Read dest.
                this._activeBufferColor.getPixelData(x, y, this._dest);
                // Write to color buffer.
                this._putPixel(x, y);
            }
            // Step x.
            cy = 0;
            cx += 1;
        }
    };
    ContextSoftware.prototype._putPixel = function (x, y) {
        this._blendModeSoftware[this._blendDestination](this._destComp, this._dest, this._dest, this._source);
        this._blendModeSoftware[this._blendSource](this._sourceComp, this._source, this._dest, this._source);
        this._rgba[0] = this._sourceComp[0] + this._destComp[0];
        this._rgba[1] = this._sourceComp[1] + this._destComp[1];
        this._rgba[2] = this._sourceComp[2] + this._destComp[2];
        this._rgba[3] = this._sourceComp[3] + this._destComp[3];
        this._activeBufferColor.setPixelData(x, y, this._rgba);
    };
    ContextSoftware.prototype.createCubeTexture = function (size, format, optimizeForRenderToTexture, streamingLevels) {
        return new CubeTextureSoftware(size);
    };
    ContextSoftware.prototype.createIndexBuffer = function (numIndices) {
        return new IndexBufferSoftware(numIndices);
    };
    ContextSoftware.prototype.createProgram = function () {
        return new ProgramSoftware();
    };
    ContextSoftware.prototype.createTexture = function (width, height, format, optimizeForRenderToTexture, streamingLevels) {
        return new TextureSoftware(width, height);
    };
    ContextSoftware.prototype.createVertexBuffer = function (numVertices, dataPerVertex) {
        return new VertexBufferSoftware(numVertices, dataPerVertex);
    };
    ContextSoftware.prototype.dispose = function () {
    };
    ContextSoftware.prototype.setBlendFactors = function (sourceFactor, destinationFactor) {
        this._blendSource = sourceFactor;
        this._blendDestination = destinationFactor;
    };
    ContextSoftware.prototype.setColorMask = function (red, green, blue, alpha) {
        this._colorMaskR = red;
        this._colorMaskG = green;
        this._colorMaskB = blue;
        this._colorMaskA = alpha;
    };
    ContextSoftware.prototype.setStencilActions = function (triangleFace, compareMode, actionOnBothPass, actionOnDepthFail, actionOnDepthPassStencilFail, coordinateSystem) {
        if (triangleFace === void 0) { triangleFace = ContextGLTriangleFace.FRONT_AND_BACK; }
        if (compareMode === void 0) { compareMode = ContextGLCompareMode.ALWAYS; }
        if (actionOnBothPass === void 0) { actionOnBothPass = ContextGLStencilAction.KEEP; }
        if (actionOnDepthFail === void 0) { actionOnDepthFail = ContextGLStencilAction.KEEP; }
        if (actionOnDepthPassStencilFail === void 0) { actionOnDepthPassStencilFail = ContextGLStencilAction.KEEP; }
        if (coordinateSystem === void 0) { coordinateSystem = CoordinateSystem.LEFT_HANDED; }
        //TODO:
    };
    ContextSoftware.prototype.setStencilReferenceValue = function (referenceValue, readMask, writeMask) {
        //TODO:
    };
    ContextSoftware.prototype.setCulling = function (triangleFaceToCull, coordinateSystem) {
        //TODO: CoordinateSystem.RIGHT_HAND
        this._cullingMode = triangleFaceToCull;
    };
    ContextSoftware.prototype.setDepthTest = function (depthMask, passCompareMode) {
        this._writeDepth = depthMask;
        this._depthCompareMode = passCompareMode;
    };
    ContextSoftware.prototype.setProgram = function (program) {
        this._program = program;
    };
    ContextSoftware.prototype.setProgramConstantsFromArray = function (programType, data) {
        var target;
        if (programType == ContextGLProgramType.VERTEX)
            target = this._vertexConstants = new Float32Array(data.length);
        else if (programType == ContextGLProgramType.FRAGMENT)
            target = this._fragmentConstants = new Float32Array(data.length);
        target.set(data);
    };
    ContextSoftware.prototype.setTextureAt = function (sampler, texture) {
        this._textures[sampler] = texture;
    };
    ContextSoftware.prototype.setVertexBufferAt = function (index, buffer, bufferOffset, format) {
        this._vertexBuffers[index] = buffer;
        this._vertexBufferOffsets[index] = bufferOffset;
        this._vertexBufferFormats[index] = format;
    };
    ContextSoftware.prototype.present = function () {
        this._backBufferColor.unlock();
        this._frontBuffer.fillRect(this._frontBuffer.rect, 0);
        this._frontBuffer.drawBitmap(this._backBufferColor.data, 0, 0, this._backBufferColor.width, this._backBufferColor.height, this._frontBufferMatrix);
        if (document && document.body) {
            this._frontBufferImageData.data.set(this._frontBuffer.data);
            this._frontBufferContext.putImageData(this._frontBufferImageData, 0, 0);
        }
    };
    ContextSoftware.prototype.drawToBitmapImage2D = function (destination) {
        destination.setPixels(this._activeBufferColor.rect, this._activeBufferColor.data);
    };
    ContextSoftware.prototype._interpolateVertexPair = function (factor, v0, v1, result) {
        for (var i = 0; i < v0.length; i++) {
            var delta = v0[i] - v1[i];
            result[i] = v1[i] + delta * factor;
        }
    };
    ContextSoftware.prototype.clamp = function (value, min, max) {
        if (min === void 0) { min = 0; }
        if (max === void 0) { max = 1; }
        return Math.max(min, Math.min(value, max));
    };
    ContextSoftware.prototype.interpolate = function (min, max, gradient) {
        return min + (max - min) * this.clamp(gradient);
    };
    ContextSoftware.prototype.drawVertices = function (mode, firstVertex, numVertices) {
        //TODO:
    };
    ContextSoftware.prototype.setScissorRectangle = function (rectangle) {
        //TODO:
    };
    ContextSoftware.prototype.setSamplerStateAt = function (sampler, wrap, filter, mipfilter) {
        var state = this._samplerStates[sampler];
        if (!state)
            state = this._samplerStates[sampler] = new SoftwareSamplerState();
        state.wrap = wrap;
        state.filter = filter;
        state.mipfilter = mipfilter;
    };
    ContextSoftware.prototype.enableStencil = function () {
    };
    ContextSoftware.prototype.disableStencil = function () {
    };
    Object.defineProperty(ContextSoftware.prototype, "frontBuffer", {
        get: function () {
            return this._frontBuffer;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ContextSoftware.prototype, "container", {
        get: function () {
            return this._canvas;
        },
        enumerable: true,
        configurable: true
    });
    ContextSoftware.prototype.clear = function (red, green, blue, alpha, depth, stencil, mask) {
        if (red === void 0) { red = 0; }
        if (green === void 0) { green = 0; }
        if (blue === void 0) { blue = 0; }
        if (alpha === void 0) { alpha = 1; }
        if (depth === void 0) { depth = 1; }
        if (stencil === void 0) { stencil = 0; }
        if (mask === void 0) { mask = ContextGLClearMask.ALL; }
        this._backBufferColor.lock();
        if (mask & ContextGLClearMask.COLOR)
            this._backBufferColor.fillRect(this._backBufferRect, ColorUtils.ARGBtoFloat32(alpha * 0xFF, red * 0xFF, green * 0xFF, blue * 0xFF));
        //TODO: mask & ContextGLClearMask.STENCIL
        if (mask & ContextGLClearMask.DEPTH)
            this._backBufferZ.set(this._backBufferZClear); //fast memcpy
    };
    ContextSoftware.prototype.finish = function () { };
    ContextSoftware.MAX_SAMPLERS = 8;
    return ContextSoftware;
}());
export { ContextSoftware };
